import { Button } from "@shared/ui";
import { INVALID_INPUT_HEADER } from "../constants";
import { stubPolicy } from "../lib/axle";
import { Field, InlineError, inputClassName } from "../components/Field";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import CarrierMark from "../components/CarrierMark";

// External dependencies
import _, { get, trim } from "lodash";
import { useEffect, useState } from "react";
import { handleStubPolicyError } from "../lib/errorHandlersUtility";

// Validation messages shown under their field rather than in the error box.
const FIELD_ERRORS = {
  "manual-policy-number": ["Enter your policy number"],
  "manual-expiration-date": [
    "Enter your expiration date",
    "Enter the expiration date as YYYY-MM-DD or DD/MM/YYYY",
    "Enter a valid expiration date",
  ],
};

const validatePolicyInput = (policyNumber, expirationDate, setError) => {
  policyNumber = trim(policyNumber);
  expirationDate = trim(expirationDate);

  if (!policyNumber) {
    setError(FIELD_ERRORS["manual-policy-number"][0]);
    return false;
  }

  if (!expirationDate) {
    setError(FIELD_ERRORS["manual-expiration-date"][0]);
    return false;
  }

  console.log("Validating expiration date:", expirationDate);

  // Support both YYYY-MM-DD and DD/MM/YYYY formats
  const yyyymmddPattern = /^\d{4}-\d{2}-\d{2}$/;
  const ddmmyyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;

  let year, month, day;

  if (yyyymmddPattern.test(expirationDate)) {
    // YYYY-MM-DD format
    [year, month, day] = expirationDate.split("-").map(Number);
  } else if (ddmmyyyyPattern.test(expirationDate)) {
    // DD/MM/YYYY format
    [day, month, year] = expirationDate.split("/").map(Number);
  } else {
    setError(FIELD_ERRORS["manual-expiration-date"][1]);
    return false;
  }

  // Validate that it's a real calendar date
  const dateObj = new Date(Date.UTC(year, month - 1, day));
  if (
    dateObj.getUTCFullYear() !== year ||
    dateObj.getUTCMonth() !== month - 1 ||
    dateObj.getUTCDate() !== day
  ) {
    setError(FIELD_ERRORS["manual-expiration-date"][2]);
    return false;
  }

  return true;
};

const ManualPolicyInfo = ({
  step,
  nextStep,
  accountInfo,
  policyInfo,
  setPolicyInfo,
  loginInformation,
  session,
  posthog,
}) => {
  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog]);

  const [policyNumber, setPolicyNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [inputType, setInputType] = useState("text");
  const [error, setError] = useState(null);

  const ignitionToken = _.chain(session).get("id").value(); //session.id;

  const hasCarrier = _.chain(loginInformation).get("carrier.id").value()
    ? true
    : false;

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validatePolicyInput(policyNumber, expirationDate, setError)) {
      return;
    }

    const constructedPolicy = {
      account: get(accountInfo, "id"),
      carrier: get(accountInfo, "carrier"),
      firstName: get(accountInfo, "firstName"),
      lastName: get(accountInfo, "lastName"),
      policyNumber,
      expirationDate: expirationDate && new Date(expirationDate).toISOString(),
    };

    const processDetail = "policy-form";
    let policyId = get(policyInfo, "id", "");

    // Save policy information
    const [stubPolicyResponse, stubPolicyResponseError] = await stubPolicy(
      ignitionToken,
      constructedPolicy,
      processDetail
    );

    if (stubPolicyResponseError) {
      handleStubPolicyError(stubPolicyResponseError, nextStep, setError);
      return;
    }

    policyId = get(stubPolicyResponse, "id");

    setPolicyInfo({ id: policyId });

    // Go to manual document upload
    nextStep("manual-policy-document");
  };

  const fieldError = Object.keys(FIELD_ERRORS).find((id) =>
    FIELD_ERRORS[id].includes(error)
  );

  return (
    <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
      <StepBody>
        <CarrierMark loginInformation={loginInformation} />
        <StepHeading
          title="Enter your policy summary"
          description={
            hasCarrier ? (
              <>
                Axle will provide this information if we are unable to connect
                with{" "}
                <span className="font-medium text-ink">
                  {_.chain(loginInformation).get("carrier.name").value()}
                </span>
                .
              </>
            ) : (
              <>Axle will use this information to verify your policy details.</>
            )
          }
        />
        <div className="flex flex-col gap-4">
          {error && !fieldError && (
            <div
              className="rounded border border-danger-border p-3"
              role="status"
            >
              <InlineError>{error}</InlineError>
            </div>
          )}
          <Field
            id="manual-policy-number"
            label="Policy number"
            error={fieldError === "manual-policy-number" ? error : undefined}
          >
            <input
              id="manual-policy-number"
              aria-invalid={fieldError === "manual-policy-number"}
              aria-describedby={fieldError === "manual-policy-number" ? "manual-policy-number-error" : undefined}
              value={policyNumber}
              className={inputClassName}
              type="text"
              onChange={(e) => setPolicyNumber(e.target.value)}
            />
          </Field>
          <Field
            id="manual-expiration-date"
            label="Expiration date"
            error={fieldError === "manual-expiration-date" ? error : undefined}
          >
            <input
              id="manual-expiration-date"
              aria-invalid={fieldError === "manual-expiration-date"}
              aria-describedby={fieldError === "manual-expiration-date" ? "manual-expiration-date-error" : undefined}
              placeholder="e.g. 04/01/2027"
              value={expirationDate}
              className={inputClassName}
              type={inputType}
              onFocus={(e) => {
                setInputType("date");
              }}
              onChange={(e) => setExpirationDate(e.target.value)}
            />
          </Field>
        </div>
      </StepBody>
      <StepFooter>
        <Button
          //onClick={() => nextStep(step)}
          type={"submit"}
        >
          Continue
        </Button>
      </StepFooter>
    </form>
  );
};

export default ManualPolicyInfo;
